import { PrimaryButton } from '@components/Buttons/PrimaryButton';
import { ModalWindow } from '@components/Modal/ModalWindow';
import { ModalWindowActions } from '@components/Modal/ModalWindowActions';
import { ModalWindowContent } from '@components/Modal/ModalWindowContent';
import { ModalWindowTitle } from '@components/Modal/ModalWindowTitle';
import { useField } from '@hooks/useField';
import { useForm } from '@hooks/useForm';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { IModalWindow } from '@hooks/useModalWindow';
import { useValidation } from '@hooks/useValidation';
import { IUsuario } from '@model/Usuario';
import { useUsuarioService } from '@services/UsuarioService';
import { yup } from '@validations/Yup';
import { AxiosResponse } from 'axios';
import React from 'react';
import { UsuarioEditarForm } from './UsuarioEditarForm';

export type UsuarioEditarModalProps = {
    modal: IModalWindow
    usuario: IUsuario
    onUsuarioAtualizado: (usuario: AxiosResponse<IUsuario>) => void
}

export function UsuarioEditarModal ({ modal, usuario, onUsuarioAtualizado }: UsuarioEditarModalProps) {
    const loading = useLoading();
    const message = useMessage();

    const form = useForm<Partial<IUsuario>>({
        nome: useField(usuario.nome),
        nomeUsuario: useField(usuario.nomeUsuario),
    })

    const validation = useValidation<IUsuario>({
        nome: yup.string().trim().required(),
        nomeUsuario: yup.string().trim().required(),
    });

    const usuarioService = useUsuarioService();

    const handleConfirm = React.useCallback(() => {
        if (!loading.isLoading) {
            const values = form.getInputValues();

            form.clearErrors();

            validation.validate(values)
                .then(() => {
                    loading.start();
                    usuarioService.editar(usuario.usuarioId, values)
                        .then(response => {
                            message.success("Usuário editado com sucesso");
                            modal.close();
                            onUsuarioAtualizado(response);
                        })
                        .catch(e => {
                            message.handleResponse(e)
                                .badRequest(form.setErrorsFromServer)
                                .showException();
                        })
                        .finally(loading.stop);
                })
                .catch(form.setErrorsFromYup);
        }
    }, [form, loading, message, modal, onUsuarioAtualizado, usuario.usuarioId, usuarioService, validation]);

    const handleEnter = React.useCallback(() => {
        form.reset();
    }, [form]);

    return (
        <ModalWindow
            modal={modal}
            disabled={loading.isLoading}
            size="md"
            TransitionProps={{
                onEnter: handleEnter
            }}
        >
            <ModalWindowTitle 
                title={`Registro de Novo Usuário`}
            />

            <ModalWindowContent>
                <UsuarioEditarForm 
                    form={form}
                />
            </ModalWindowContent>

            <ModalWindowActions>
                <PrimaryButton onClick={handleConfirm}>Editar Usuário</PrimaryButton>
            </ModalWindowActions>
        </ModalWindow>
    )
}